import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { BuilderDayLayout } from "../../lib/metadata-builder/day-layouts";
import { artworkPlacement } from "../../lib/metadata-builder/artwork-placement";
import { dispatchMetadataEvent, endMetadataMerge } from "../../lib/metadata-history/dispatch";
import type { MetadataDay } from "../../lib/metadata-history/metadata-targets";
import { DaysSchema } from "../../lib/schedule-presets/art-metadata";
import { MetadataField } from "../editor-panel/developer-section/MetadataField";
import { metadataLabels } from "../editor-panel/developer-section/metadata-form-schema";
import { Button } from "../ui/button";

function samePoint(left: number[], right: number[]) {
  const sameLength = left.length === right.length;
  if (!sameLength) return false;
  return left.every((value, index) => value === right[index]);
}

type DayAnchorControlsProps = { layout: BuilderDayLayout; day: MetadataDay; source: string };

export function DayAnchorControls({ layout, day, source }: DayAnchorControlsProps) {
  const { t } = useTranslation();
  const labels = metadataLabels(t);
  const suggestion = useMemo(() => artworkPlacement(source), [source]);
  const anchor = layout.anchors[day];
  const usingSuggestion = samePoint(anchor, suggestion.point);
  const title = layout.mode === "offline" ? t("builder.offline.anchor") : t("builder.stream.anchor");
  return (
    <div className="space-y-1.5" onBlurCapture={endMetadataMerge}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h4 className="text-xs font-medium">{title}</h4>
        <Button size="xs" variant="ghost" disabled={usingSuggestion} className="font-normal text-neutral-500 hover:text-neutral-950" onClick={() => {
          endMetadataMerge();
          dispatchMetadataEvent({ type: "position/set", path: `days.${day}.origin`, point: suggestion.point });
        }}>{suggestion.suggested ? t("builder.offline.useSuggestion") : t("builder.offline.useCenter")}</Button>
      </div>
      <MetadataField schema={DaysSchema.shape.mon.shape.origin} value={anchor}
        label={labels.origin!} labels={labels} onChange={value => {
          const point = DaysSchema.shape.mon.shape.origin.parse(value);
          dispatchMetadataEvent({ type: "position/set", path: `days.${day}.origin`, point }, `${layout.mode}:${day}:anchor`);
        }} />
      <p className="text-neutral-500">{t("builder.days.anchorHelp")}</p>
    </div>
  );
}
